import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ConfirmDelete, DetailRow, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ConfirmAction } from '@/components/confirm';
import { BackLink, recordGate } from '@/components/record';
import AnnouncementDialog, { LIVE_STYLES, SHOW_ON_LABELS, liveState } from '@/components/admin/AnnouncementDialog';
import { TONE_STYLES } from '@/shared/constants';
import { ANNOUNCEMENT_FIELDS } from '@/shared/fields';
import { committed } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';
import { cn } from '@/lib/utils';

export const route = { path: '/admin/announcements/:id', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };

const TONE_ICONS = { Info: 'Info', Promo: 'Sparkles', Outage: 'TriangleAlert' };

export default function AdminAnnouncementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const { data: item, loading, error, run } = useFetch(
    () => sdk.table('announcements_c').select(ANNOUNCEMENT_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const back = <BackLink to="/admin/announcements" label="Back to Announcements" />;
  const gate = recordGate({ back, loading, record: item, error, run, what: 'Announcement' });
  if (gate) return gate;

  const state = liveState(item);

  async function toggle() {
    const next = !item.active_c;
    const res = await sdk.table('announcements_c').update({ Id: item.Id, active_c: next });
    if (!committed(res, next ? 'Announcement is live' : 'Announcement taken down')) return false;
    run();
    return true;
  }

  async function remove() {
    const res = await sdk.table('announcements_c').remove(item.Id);
    if (!committed(res, 'Announcement deleted')) return false;
    navigate('/admin/announcements');
    return true;
  }

  return (
    <div className="mx-auto max-w-3xl">
      {back}
      <PageHeader
        eyebrow={`Announcement · ${formatLocalDate(item.CreatedOn, DATE_FORMATS.SHORT)}`}
        title="Banner"
        actions={
          <>
            <StatusBadge value={state} styles={LIVE_STYLES} />
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <ApperIcon name="Pencil" size={16} />
              Edit
            </Button>
            <ConfirmDelete title="Delete this announcement?" description="It comes down everywhere and cannot be restored." onConfirm={remove} />
          </>
        }
      />

      <Panel title="Preview" icon="Eye" bodyClassName="py-4">
        <div className={cn('flex items-center gap-3 rounded-md border px-4 py-3 text-sm', TONE_STYLES[item.tone_c])}>
          <ApperIcon name={TONE_ICONS[item.tone_c] ?? 'Info'} size={16} />
          <span className="min-w-0 flex-1">{item.Name}</span>
        </div>
      </Panel>

      <div className="my-4 flex flex-wrap gap-2">
        <ConfirmAction
          label={item.active_c ? 'Take down' : 'Put live'}
          icon={item.active_c ? 'EyeOff' : 'Eye'}
          title={item.active_c ? 'Take this banner down?' : 'Put this banner live?'}
          description={
            item.active_c
              ? 'Visitors stop seeing it on the next page load.'
              : `It shows on the ${(SHOW_ON_LABELS[item.show_on_c] ?? 'site').toLowerCase()} until it ends.`
          }
          busyLabel="Saving…"
          onConfirm={toggle}
        />
      </div>

      <Panel title="Details" icon="Info">
        <dl className="divide-y divide-border">
          <DetailRow label="Message">{item.Name}</DetailRow>
          <DetailRow label="Tone">
            <StatusBadge value={item.tone_c} styles={TONE_STYLES} />
          </DetailRow>
          <DetailRow label="Shows on">{SHOW_ON_LABELS[item.show_on_c] ?? item.show_on_c}</DetailRow>
          <DetailRow label="Switched on">{item.active_c ? 'Yes' : 'No'}</DetailRow>
          <DetailRow label="Ends">{item.ends_at_c ? formatLocalDate(item.ends_at_c, DATE_FORMATS.WITH_TIME) : 'Until taken down'}</DetailRow>
          <DetailRow label="Posted">{formatLocalDate(item.CreatedOn, DATE_FORMATS.WITH_TIME)}</DetailRow>
        </dl>
      </Panel>

      <AnnouncementDialog open={editOpen} onOpenChange={setEditOpen} announcement={item} onSaved={run} />
    </div>
  );
}
